import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, money } from "@/lib/api";
import { Sparkles, ArrowRight, ArrowLeft, RefreshCw, Heart, Gift } from "lucide-react";

const QUESTIONS = [
  {
    id: "who",
    title: "Who are you shopping for?",
    options: [
      { emoji: "💚", label: "For Me", tags: {}, tone: "for yourself" },
      { emoji: "🎁", label: "Someone I Love", tags: {}, tone: "for someone you love" },
      { emoji: "🌼", label: "Someone Going Through a Difficult Season", tags: { "cancer-illness": 1, "grief-loss": 1, "recovery": 1 }, tone: "for someone in a hard season" },
      { emoji: "🤝", label: "Someone I Want to Encourage", tags: { "faith-encouragement": 2 }, tone: "to encourage someone" },
    ],
  },
  {
    id: "encouragement",
    title: "What kind of encouragement are they looking for?",
    options: [
      { emoji: "🕊️", label: "Peace", tags: { "mental-health": 2 } },
      { emoji: "🌈", label: "Hope", tags: { "faith-encouragement": 2 } },
      { emoji: "🤍", label: "Comfort", tags: { "grief-loss": 2 } },
      { emoji: "💪", label: "Strength", tags: { "cancer-illness": 2 } },
      { emoji: "🌿", label: "Healing", tags: { "recovery": 2 } },
      { emoji: "🙏", label: "Gratitude", tags: { "faith-encouragement": 1 } },
      { emoji: "🛁", label: "Self-Care", tags: { "mental-health": 1, "recovery": 1 } },
      { emoji: "✝️", label: "Faith", tags: { "faith-encouragement": 2 }, box: "faith" },
    ],
  },
  {
    id: "today",
    title: "Which statement best describes today?",
    options: [
      { emoji: "🌱", label: "I'm starting a new chapter.", tags: { "faith-encouragement": 2 }, box: "strength" },
      { emoji: "🌧️", label: "I'm feeling overwhelmed.", tags: { "mental-health": 2, "caregiving": 1 }, box: "anxiety" },
      { emoji: "🎉", label: "I'm celebrating a milestone.", tags: { "faith-encouragement": 1 }, box: "thinking" },
      { emoji: "🌞", label: "I want to brighten someone's day.", tags: { "faith-encouragement": 1 }, box: "thinking" },
      { emoji: "🫶", label: "I simply need a reminder that everything will be okay.", tags: { "mental-health": 1, "faith-encouragement": 1 } },
    ],
  },
  {
    id: "difference",
    title: "What would make the biggest difference?",
    options: [
      { emoji: "🧘", label: "Relaxation", tags: { "mental-health": 1, "recovery": 1 } },
      { emoji: "💡", label: "Inspiration", tags: { "faith-encouragement": 1 } },
      { emoji: "🙏", label: "Prayer", tags: { "faith-encouragement": 2 }, box: "faith" },
      { emoji: "🗂️", label: "Organization", tags: { "digital": 2 } },
      { emoji: "📓", label: "Journaling", tags: { "digital": 2 } },
      { emoji: "📅", label: "Daily encouragement", tags: { "faith-encouragement": 1 } },
      { emoji: "🎒", label: "Practical essentials", tags: { "recovery": 1, "cancer-illness": 1 } },
    ],
  },
];

const COLLECTION_NAME = {
  "cancer-illness": "Cancer & Illness",
  "grief-loss": "Grief & Loss",
  "mental-health": "Mental Health",
  "recovery": "Recovery",
  "caregiving": "Caregiving",
  "faith-encouragement": "Faith & Encouragement",
  "digital": "Digital Collection",
};
const PRIORITY = ["grief-loss", "cancer-illness", "recovery", "mental-health", "caregiving", "faith-encouragement", "digital"];
const BOX_KEYWORD = { faith: "faith and prayer", strength: "strength and hope", thinking: "thinking of you", anxiety: "anxiety relief" };

export default function KitFinder() {
  const [products, setProducts] = useState([]);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});

  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)).catch(() => {}); }, []);

  const total = QUESTIONS.length;
  const done = step >= total;

  const choose = (opt) => {
    const q = QUESTIONS[step];
    setAnswers((a) => ({ ...a, [q.id]: opt }));
    setTimeout(() => setStep((s) => s + 1), 180);
  };

  const result = useMemo(() => {
    if (!done || !products.length) return null;
    const score = {};
    const boxHints = [];
    QUESTIONS.forEach((q) => {
      const opt = answers[q.id];
      if (!opt) return;
      Object.entries(opt.tags || {}).forEach(([k, v]) => { score[k] = (score[k] || 0) + v; });
      if (opt.box) boxHints.push(opt.box);
    });
    let top = PRIORITY.find((k) => k in score) || "faith-encouragement";
    let best = -1;
    PRIORITY.forEach((k) => { if ((score[k] || 0) > best) { best = score[k] || 0; top = k; } });

    if (top === "digital") {
      const dig = products.find((p) => p.product_type === "digital");
      if (dig) return { product: dig, collection: "digital", to: "/digital-downloads" };
      top = "faith-encouragement";
    }

    const boxes = products.filter((p) => p.product_type === "box" && p.collection_key === top);
    let pick = null;
    for (const h of boxHints) {
      const kw = BOX_KEYWORD[h];
      if (kw) { const m = boxes.find((b) => b.name.toLowerCase().includes(kw)); if (m) { pick = m; break; } }
    }
    if (!pick) pick = boxes[0] || products.find((p) => p.product_type === "box");
    return pick ? { product: pick, collection: top, to: `/product/${pick.slug}` } : null;
  }, [done, products, answers]);

  const restart = () => { setAnswers({}); setStep(0); };
  const tone = answers.who?.tone || "for someone special";
  const pct = Math.round((Math.min(step, total) / total) * 100);

  return (
    <div className="min-h-screen bg-[#F2EFEB]" data-testid="kit-finder-page">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
        <div className="text-center">
          <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase font-semibold text-[#8E9A86] bg-white border border-[#D9CBA0]/60 rounded-full px-4 py-1.5">
            <Sparkles className="h-3.5 w-3.5" /> 60-second Kit Finder
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl text-[#3A3632] mt-5">Find Your Perfect Hope &amp; Favor Kit</h1>
          <p className="text-[#6B655F] mt-3 font-light">Answer four quick questions and we'll suggest the most meaningful kit.</p>
        </div>

        {!done && (
          <div className="mt-10">
            <div className="flex items-center justify-between text-xs text-[#8A8175] mb-2">
              <span>Question {step + 1} of {total}</span><span>{pct}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-white overflow-hidden" data-testid="kf-progress">
              <div className="h-full bg-[#8E9A86] transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>

            <div className="mt-8 bg-white rounded-2xl border border-[#E5DFD5] p-6 sm:p-9" data-testid={`kf-step-${step}`}>
              <h2 className="font-heading text-2xl sm:text-3xl text-[#3A3632] text-center">{QUESTIONS[step].title}</h2>
              <div className="grid sm:grid-cols-2 gap-3 mt-7">
                {QUESTIONS[step].options.map((opt, i) => {
                  const selected = answers[QUESTIONS[step].id]?.label === opt.label;
                  return (
                    <button key={i} onClick={() => choose(opt)} data-testid={`kf-option-${step}-${i}`}
                      className={`flex items-center gap-3 text-left rounded-xl border p-4 transition-colors ${selected ? "border-[#8E9A86] bg-[#8E9A86]/8" : "border-[#E5DFD5] hover:border-[#D9CBA0] hover:bg-[#FBF7F0]"}`}>
                      {opt.emoji && <span className="text-2xl leading-none shrink-0">{opt.emoji}</span>}
                      <span className="text-sm font-medium text-[#3A3632]">{opt.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {step > 0 && (
              <button onClick={() => setStep((s) => s - 1)} data-testid="kf-back"
                className="mt-5 inline-flex items-center gap-1.5 text-sm text-[#8A8175] hover:text-[#8E9A86] transition-colors">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
            )}
          </div>
        )}

        {done && (
          <div className="mt-10" data-testid="kf-result">
            {result ? (
              <div className="bg-white rounded-2xl border border-[#D9CBA0]/50 shadow-sm overflow-hidden">
                <div className="text-center pt-8 px-6">
                  <span className="font-hand text-2xl text-[#C4A4A4]">Your perfect match {tone}</span>
                </div>
                <div className="grid sm:grid-cols-2 gap-0 items-stretch mt-4">
                  <div className="bg-[#FBF7F0]">
                    {result.product.image && <img src={result.product.image} alt={result.product.name} className="w-full h-full object-cover min-h-[240px]" />}
                  </div>
                  <div className="p-7 flex flex-col">
                    <span className="gh-overline">{COLLECTION_NAME[result.collection]}</span>
                    <h3 className="font-heading text-3xl text-[#3A3632] mt-2 leading-tight">{result.product.name}</h3>
                    <p className="text-sm text-[#6B655F] mt-3 leading-relaxed flex-1">{result.product.short_desc || result.product.description}</p>
                    <div className="font-heading text-3xl text-[#8E9A86] mt-4">{money(result.product.price)}</div>
                    <Link to={result.to} data-testid="kf-view-kit" className="btn-primary mt-5 inline-flex justify-center">
                      View this kit <ArrowRight className="h-4 w-4" />
                    </Link>
                    <div className="flex items-center justify-center gap-5 mt-4">
                      <button onClick={restart} data-testid="kf-retake" className="inline-flex items-center gap-1.5 text-sm text-[#8A8175] hover:text-[#8E9A86] transition-colors"><RefreshCw className="h-3.5 w-3.5" /> Retake quiz</button>
                      <Link to="/shop" data-testid="kf-browse-all" className="gh-link-underline text-sm text-[#8E9A86]">Browse all kits</Link>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-[#E5DFD5] p-9 text-center">
                <p className="text-[#6B655F]">Let's find the right fit — <Link to="/shop" className="text-[#8E9A86] gh-link-underline">browse all care boxes</Link>.</p>
                <button onClick={restart} className="mt-4 inline-flex items-center gap-1.5 text-sm text-[#8A8175]"><RefreshCw className="h-3.5 w-3.5" /> Retake quiz</button>
              </div>
            )}

            {/* Continue Your Journey */}
            <div className="mt-8 rounded-2xl bg-[#3A3632] text-[#FAF7F2] p-8 sm:p-10 text-center" data-testid="kf-membership">
              <Gift className="h-7 w-7 text-[#D9CBA0] mx-auto" />
              <h3 className="font-heading text-2xl sm:text-3xl mt-4">Continue Your Journey</h3>
              <p className="text-[#D9CBA0]/90 mt-3 max-w-xl mx-auto leading-relaxed font-light">
                Receive a new Hope &amp; Favor box every month, thoughtfully curated with uplifting products, encouragement, and meaningful surprises delivered to your door.
              </p>
              <Link to="/monthly-club" data-testid="kf-join-membership"
                className="inline-flex items-center gap-2 mt-7 bg-[#D9CBA0] text-[#3A3632] rounded-full px-8 py-3 font-medium hover:bg-[#e6dab8] transition-colors">
                <Heart className="h-4 w-4" /> Join the Hope &amp; Favor Monthly Membership
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
